import {LogLevel} from "@azure/msal-browser";

const isTestEnv = window.location.hostname.includes('test');

/**
 * Where the B2C settings come from, in order:
 *
 * 1. `window.huMsalConfig`, rendered by layout.antlers.html from `services.azure_b2c`
 *    (config/services.php): deploy-time env, so one bundle serves every environment.
 * 2. Build-time `process.env.MIX_AZURE_B2C_*`, the mechanism this replaces, kept as a
 *    fallback for the transition. Laravel Mix substitutes those only when the `.env` of
 *    the machine that builds has them. The production deploy builds on its server and
 *    that `.env` carries the production trio, so production has been working this way;
 *    the test server's `.env` has no `_DEV` trio and the committed bundles were built
 *    with none at all, so there MSAL got `undefined` for all three and threw on the
 *    authority (Sentry HUOPENAIR-2M) on every page load and every click on "Accedi".
 *
 * Nothing configured = `msalConfigured` false: auth.js then never constructs MSAL and
 * the login UI stays inert instead of throwing. Keep the `process.env.MIX_…` member
 * expressions literal below, that is what Mix's DefinePlugin matches.
 */
function fromBuildEnv() {
    return isTestEnv
        ? {
            clientId: process.env.MIX_AZURE_B2C_CLIENT_ID_DEV,
            authority: process.env.MIX_AZURE_B2C_AUTHORITY_DEV,
            knownAuthority: process.env.MIX_AZURE_B2C_KNOW_AUTHORITIES_DEV,
        }
        : {
            clientId: process.env.MIX_AZURE_B2C_CLIENT_ID,
            authority: process.env.MIX_AZURE_B2C_AUTHORITY,
            knownAuthority: process.env.MIX_AZURE_B2C_KNOW_AUTHORITIES,
        };
}

const server = window.huMsalConfig || fromBuildEnv();

export const msalConfigured = Boolean(server.clientId && server.authority);

export const msalConfig = {
    auth: {
        clientId: server.clientId,
        authority: server.authority,
        knownAuthorities: server.knownAuthority ? [server.knownAuthority] : [],
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
    },
    cache: {
        // localStorage, not sessionStorage: a session started in one tab is then
        // known in every other tab and after a reload without a round trip to B2C,
        // so auth.js can report "logged in" from the cache at once (the BOL does the
        // same). MSAL still refreshes tokens on use; an expired one just means the
        // next token request goes through ssoSilent again.
        cacheLocation: "localStorage",
    },
    system: {
        loggerOptions: {
            // Trace plus a console.log callback printed MSAL's internals on every
            // page of the live site: dozens of Trace/Verbose lines per load, for
            // everyone, with nothing actionable in them. Production keeps what
            // deserves a look (warnings and errors, each on the console channel
            // that matches); the test hosts keep Verbose so a login can still be
            // followed while debugging.
            loggerCallback(logLevel, message) {
                if (logLevel === LogLevel.Error) {
                    console.error(message);
                } else if (logLevel === LogLevel.Warning) {
                    console.warn(message);
                } else {
                    console.debug(message);
                }
            },
            logLevel: isTestEnv ? LogLevel.Verbose : LogLevel.Warning,
            piiLoggingEnabled: false
        }
    }
};
